import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IRoleCreationDTO } from '@common-frontend-submodule/interfaces/role/irole-creation.interface';
import { IRoleUpdateDTO } from '@common-frontend-submodule/interfaces/role/irole-update.interface';
import { IGetRolesRequestDTO } from '@common-frontend-submodule/interfaces/role/iget-roles-request.interface';
import { IRoleDTO } from '@common-frontend-submodule/interfaces/role/irole.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { RoleRoutes } from '@common-frontend-submodule/enums/routes/role-routes.enum';
import { IRoleShortDTO } from '@common-frontend-submodule/interfaces/role/irole-short.interface';

const apiClient = ApiClient.getInstance()

interface RoleContextProps {
  getById: (roleId: string) => Promise<IApiResponseGenericDTO<IRoleDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<IRoleShortDTO[]>>;
  filter: (model: IGetRolesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IRoleDTO>>>;
  create: (model: IRoleCreationDTO) => Promise<IApiResponseGenericDTO<IRoleDTO>>;
  update: (roleId: string, model: IRoleUpdateDTO) => Promise<IApiResponseGenericDTO<IRoleDTO>>;
  delete: (roleId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const RoleContext = createContext<RoleContextProps | undefined>(undefined);

export const RoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (roleId: string): Promise<IApiResponseGenericDTO<IRoleDTO>> => {
    return await apiClient.getRequest<IRoleDTO>(`${RoleRoutes.BasePrefix}/${roleId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IRoleShortDTO[]>> => {
    return await apiClient.getRequest<IRoleShortDTO[]>(`${RoleRoutes.BasePrefix}/${RoleRoutes.All}`);
  };

  const filter = async (model: IGetRolesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IRoleDTO>>> => {
    return await apiClient.postRequest<IGetRolesRequestDTO, IPaginatedResultDTO<IRoleDTO>>(
      `${RoleRoutes.BasePrefix}/${RoleRoutes.List}`,
      model
    );
  };

  const create = async (model: IRoleCreationDTO): Promise<IApiResponseGenericDTO<IRoleDTO>> => {
    return await apiClient.postRequest<IRoleCreationDTO, IRoleDTO>(RoleRoutes.BasePrefix, model);
  };

  const update = async (roleId: string, model: IRoleUpdateDTO): Promise<IApiResponseGenericDTO<IRoleDTO>> => {
    return await apiClient.putRequest<IRoleUpdateDTO, IRoleDTO>(`${RoleRoutes.BasePrefix}/${roleId}`, model);
  };

  const deleteRole = async (roleId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${RoleRoutes.BasePrefix}/${roleId}`);
  };

  return (
    <RoleContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteRole }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRoleStore = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleStore must be used within a RoleProvider');
  }
  return context;
};
